import React, { useState } from 'react';

// SVG Icons (same as VendorViewModal)
const CloseIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const MaximizeIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
  </svg>
);

const MinimizeIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
  </svg>
);

const ApiMappingViewModal = ({ open, onClose, data }) => {
  const [isMaximized, setIsMaximized] = useState(false);

  if (!open || !data) return null;

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  // Color-coded badge for HTTP Method
  const getMethodBadge = (method) => {
    const styles = {
      GET: 'bg-blue-100 text-blue-800',
      POST: 'bg-green-100 text-green-800',
      PUT: 'bg-amber-100 text-amber-800',
      PATCH: 'bg-indigo-100 text-indigo-800',
      DELETE: 'bg-red-100 text-red-800',
    };
    const defaultStyle = 'bg-gray-100 text-gray-800';
    const className = styles[method?.toUpperCase()] || defaultStyle;

    return (
      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold shadow-sm uppercase ${className}`}>
        {method || '-'}
      </span>
    );
  };

  // Define sections similar to VendorViewModal
  const sections = [
    {
      title: 'API Configuration',
      fields: [
        { label: 'ID', value: data.Id || '-' },
        { label: 'API Name', value: data.ApiName || '-' },
        { label: 'Base URL', value: data.BaseUrl || '-', breakAll: true },
        { label: 'HTTP Method', value: getMethodBadge(data.HttpMethod) },
        { label: 'Endpoint', value: data.Endpoint || '-', breakAll: true },
      ],
    },
    {
      title: 'Authentication Details',
      fields: [
        { label: 'Token URL', value: data.TokenUrl || '-', breakAll: true },
        { label: 'Client ID', value: data.ClientId || '-' },
        { label: 'Client Secret', value: data.ClientSecret ? '••••••••' : '-', masked: true },
        { label: 'Scope', value: data.Scope || '-' },
        { label: 'Grant Type', value: data.GrantType || '-' },
        { label: 'User ID', value: data.UserId || '-' },
        { label: 'Password', value: data.Password ? '••••••••' : '-', masked: true },
      ],
    },
    {
      title: 'System Information',
      fields: [
        { label: 'Created At', value: data.CreatedAt ? new Date(data.CreatedAt).toLocaleString() : '-' },
        { label: 'Updated At', value: data.UpdatedAt ? new Date(data.UpdatedAt).toLocaleString() : '-' },
      ],
    },
  ];

  return (
    <div className={`fixed inset-0 z-50 flex transition-all duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Side drawer from right */}
      <div
        className={`relative ml-auto bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-in-out
          ${open ? 'translate-x-0' : 'translate-x-full'}
          ${isMaximized ? 'w-full h-full max-w-none rounded-none' : 'w-full max-w-4xl h-full'}`}
      >
        {/* Header */}
        <div className="flex-shrink-0 p-8 pb-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">API Mapping Details</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={toggleMaximize}
                className="p-2 text-[#A0AEC0] hover:text-[#384959] transition-colors"
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon /> : <MaximizeIcon />}
              </button>
              <button
                onClick={onClose}
                className="p-2 text-[#A0AEC0] hover:text-[#384959] transition-colors"
                title="Close"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-grow overflow-y-auto px-8 py-6 custom-scrollbar">
          <div className="space-y-8 pb-8">
            {sections.map((section, idx) => (
              <div key={idx} className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-5">{section.title}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {section.fields.map((field, fieldIdx) => (
                    <div key={fieldIdx} className="flex flex-col">
                      <span className="text-sm font-medium text-[#64748b] mb-1">
                        {field.label}
                      </span>
                      <div className={`text-base font-semibold text-[#384959] break-words ${field.breakAll ? 'break-all' : ''}`}>
                        {field.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex-shrink-0 p-8 pt-6 border-t border-gray-200 bg-white">
          <div className="flex justify-end gap-4">
            <button
              onClick={onClose}
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all hover:bg-[#F1F5F9]"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Custom scrollbar styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
};

export default ApiMappingViewModal;